import { get } from 'lodash';
import getMetricsData from '@api/metricContainer';
import instance from '@api/axiosInstance';
import useFrustrationSignalQuery, {
  createFrustrationSignalQuery,
} from './useFrustrationSignalQuery';
import useQueryParams from '../../hooks/useQueryParams';
import { formatTimeseriesData } from '@src/components/metrics-panel-hz/utils/format';
import { panelsAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';

const getFrustrationSignalMetrics = (signalMeta: any) => {
  try {
    if (!signalMeta?.metrics) {
      return [];
    }

    const { count, with_user, with_session } = signalMeta.metrics;
    return [count, with_user, with_session].filter(Boolean);
  } catch (error) {
    // Error getting frustration signal metrics
    return [];
  }
};

function useFrustrationSignalData({
  signalId,
  signalMeta,
}: {
  signalId: string;
  signalMeta?: any;
}) {
  const panels = useAtomValue(panelsAtom);
  const metrics = getFrustrationSignalMetrics(signalMeta);
  const { interval, filter, _rKey, allowedMetrics } = useQueryParams();

  const isDeleted = signalMeta?.isDeleted;

  const filteredMetrics = metrics.filter(
    (metric) => metric && allowedMetrics && metric in allowedMetrics,
  );

  // Get percentage metrics for the allowed metrics
  const percentageMetrics = filteredMetrics
    .map((metric) => getPercentageMetricByKey({ metricKey: metric }))
    .filter(Boolean)
    .filter((metric) => allowedMetrics && metric in allowedMetrics);

  // Combine base metrics with their percentage counterparts
  const allowedMetricList = [...new Set([...filteredMetrics, ...percentageMetrics])];
  const options = {};
  const payload = createFrustrationSignalQuery(
    allowedMetricList,
    `${interval.from}/${interval.to}`,
    interval.granularity,
    filter,
    options,
  );

  const result = useFrustrationSignalQuery({
    signalId: signalId,
    queryKey: ['overview', 'frustration-signal', signalId, payload.queries, _rKey],
    queryFn: () =>
      getMetricsData(payload, {}, instance).then((response) => {
        const { data, totals, annotations } = get(response, ['data', '0'], {
          totals: {},
          data: [],
          annotations: [],
        });
        const formattedData = formatTimeseriesData({
          metrics: allowedMetricList,
          data,
          interval,
          annotations,
        });
        return { data: formattedData, totals };
      }),
    options: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false, // Prevent refetch when panel is shown
      enabled: !!metrics.length && !isDeleted && panels.frustration,
      keepPreviousData: true, // Preserve data when panel is hidden/shown
      staleTime: 5 * 60 * 1000, // 5m
    },
  });

  if (isDeleted) {
    return {
      data: null,
      isLoading: false,
      isFetching: false,
      isError: false,
      error: null,
      refetch: () => Promise.resolve(),
      queued: false,
    };
  }

  return result;
}

export default useFrustrationSignalData;
